/**
 * This file is temporarily placed here for development purposes.
 * It will be moved into a shared package to be used across other projects.
 */

import { create } from 'zustand';
import { signIn, confirmSignIn, type SignInOutput, fetchAuthSession, getCurrentUser, signOut } from 'aws-amplify/auth';
import { Amplify } from 'aws-amplify';
import { jwtDecode } from 'jwt-decode';
import { decodeOAuthState, encodeOAuthState } from '../../lib/auth/oauthState';
import { WrongTenantError } from './wrongTenantError';
import { consumePendingSsoState, createPendingSsoState } from '../../lib/storage/ssoStateStorage';
import {
  clearForceReauth,
  clearTenantAuthSession,
  disableAutoLogin,
  // Aliased: initialize() has a local `enableAutoLogin` variable.
  enableAutoLogin as allowAutoLogin,
  isAutoLoginDisabled,
  isForceReauthRequired,
  purgeLegacyAutoLoginFlag,
  requireForceReauth,
} from '../../lib/storage/authStorage';

export type User = {
  email?: string;
  sub?: string;
  name?: string;
  exp?: number;
  [key: string]: unknown;
};

export type FederatedAuthOptions = {
  enableAutoLogin?: boolean;
  enableAutoTokenRefetch?: boolean;
  cognitoDomain: string;
  userPoolId: string;
  userPoolClientId: string;
  redirectUri: string;
  identityProvider: string;
  customScope?: string;
  tenantId: string;
};

export type LoginStatus = 'loggedOut' | 'waitingForAuthCode' | 'loggingIn' | 'loggedIn' | 'otpRequested';

export type OtpRequestInfo = {
  username: string;
  otpResponse: SignInOutput;
  expiry: Date;
  receivedAt: Date;
};

type AuthState = {
  accessToken: string | null;
  refreshToken: string | null;
  isLoading: boolean;
  error: string | null;
  config: FederatedAuthOptions | null;
  loginStatus: LoginStatus;
  otpRequestInfo: OtpRequestInfo | null;
  hasInitialized: boolean;
  pendingAuthCode: string | null;

  initialize: (config: FederatedAuthOptions) => Promise<void>;
  login: () => void;
  logout: () => void;
  clearInvalidSession: () => Promise<void>;
  submitAuthCode: (code: string, state?: string | null) => Promise<void>;
  exchangeCodeForToken: (code: string) => Promise<void>;
  refreshAccessToken: () => Promise<void>;
  requestOTP: (username: string) => Promise<SignInOutput>;
  signInWithOTP: (code: string) => Promise<SignInOutput>;
  resetOtpRequestInfo: () => void;
  setError: (error: string | null) => void;
};

export const useAuthStore = create<AuthState>((set, get) => {
  let refreshTimeout: ReturnType<typeof setTimeout> | null = null;

  const scheduleTokenRefresh = () => {
    const { accessToken, config, refreshToken } = get();
    if (accessToken && config?.enableAutoTokenRefetch && refreshToken) {
      const decoded = jwtDecode<User>(accessToken);
      if (decoded.exp) {
        const nowInSeconds = Date.now() / 1000;
        const expiresIn = decoded.exp - nowInSeconds;
        const refreshDelay = (expiresIn - 60) * 1000;
        if (refreshTimeout) {
          clearTimeout(refreshTimeout);
        }
        if (refreshDelay > 0) {
          refreshTimeout = setTimeout(() => {
            get().refreshAccessToken();
          }, refreshDelay);
        } else {
          get().refreshAccessToken();
        }
      }
    }
  };

  return {
    user: null,
    accessToken: null,
    refreshToken: null,
    isLoading: false,
    error: null,
    config: null,
    loginStatus: 'loggedOut',
    otpRequestInfo: null,
    hasInitialized: false,
    pendingAuthCode: null,

    initialize: async (config: FederatedAuthOptions) => {
      let enableAutoLogin = config.enableAutoLogin;
      if (
        get().hasInitialized ||
        (sessionStorage.getItem(`${config.tenantId}_authStore_Initialized`) === 'true' && !config.enableAutoLogin) ||
        isAutoLoginDisabled(config.tenantId)
      ) {
        enableAutoLogin = false;
        console.log('Auto-login is disabled.');
      }

      // The flag used to be stored in localStorage, where it never expired and could not be cleared
      // from within the app. Drop any leftover value so existing profiles recover (HRI2-17930).
      purgeLegacyAutoLoginFlag(config.tenantId);

      sessionStorage.setItem(`${config.tenantId}_authStore_Initialized`, 'true');

      Amplify.configure({
        Auth: {
          Cognito: {
            userPoolId: config.userPoolId,
            userPoolClientId: config.userPoolClientId,
          },
        },
      });

      set({ config, isLoading: true });

      const currentUser = await getCurrentUser().catch(() => null);
      let isUsingSameSession = false;

      if (currentUser) {
        const session = await fetchAuthSession({ forceRefresh: true });
        const sessionTenantCode = session.tokens?.accessToken?.payload?.['custom:tenantCode'];
        isUsingSameSession = sessionTenantCode === config.tenantId;

        if (isUsingSameSession) {
          set({
            accessToken: session.tokens?.accessToken.toString(),
            refreshToken: null,
            loginStatus: 'loggedIn',
          });

          if (config.enableAutoTokenRefetch) {
            scheduleTokenRefresh();
          }
        } else {
          console.warn('Warning: The current session does not match the expected tenant.');
        }
      }

      if (enableAutoLogin && (!currentUser || !isUsingSameSession)) {
        console.log('Auto-login enabled, triggering login...');
        set({ loginStatus: 'waitingForAuthCode' });
        set({ hasInitialized: true });
        get().login();
      }

      scheduleTokenRefresh();
      set({ isLoading: false, hasInitialized: true });
    },

    login: () => {
      const { config } = get();
      if (!config) {
        console.error('No config provided for login.');
        return;
      }
      // Nothing clears `error` on its own, and the login form renders it. Without this a stale
      // background failure (a token refresh, or a previous rejected callback) would sit on the
      // form forever with no way for the user to dismiss it.
      set({ loginStatus: 'loggingIn', error: null });

      // The Cognito app client is shared across tenants and we cannot register per-tenant
      // callback URLs, so redirect_uri has to stay the bare origin. The tenant travels in the
      // OAuth `state` parameter instead, which survives the round-trip even where sessionStorage
      // does not (HRI2-16956).
      let state: string;
      try {
        state = encodeOAuthState({ tenantId: config.tenantId, nonce: createPendingSsoState(config.tenantId) });
      } catch (error) {
        // Minting `state` needs `crypto.getRandomValues` and `btoa`, either of which can throw
        // (no crypto global, or a tenant code outside Latin1). Stop here with a visible error
        // instead of throwing out of `login()` and leaving the form stuck on a spinner.
        //
        // We deliberately do not fall back to a stateless authorize URL: `submitAuthCode` now
        // requires `state`, so such a request would come back only to be rejected, costing a
        // pointless round-trip through the IdP and reporting a misleading "could not be
        // verified" error instead of the real cause.
        console.error('Could not build the OAuth state parameter; sign-in cannot start.', error);
        set({
          error: 'Sign-in could not be started. Please try again or contact support.',
          loginStatus: 'loggedOut',
        });
        return;
      }

      // Cognito's /logout only clears its own hosted-UI cookie, so the upstream IdP session stays
      // live and would silently re-assert the same identity - making "sign in as someone else" a
      // slower route back to the same error. `prompt=login` forces the IdP to re-authenticate.
      //
      // One attempt only: prompting on every sign-in would defeat normal SSO auto-login, so the
      // flag is consumed here, once we know we are actually redirecting.
      const forceReauth = isForceReauthRequired(config.tenantId);
      if (forceReauth) {
        clearForceReauth(config.tenantId);
      }

      const authUrl = `${config.cognitoDomain}/oauth2/authorize?client_id=${config.userPoolClientId}&response_type=code&scope=openid+email+profile+${
        config.customScope || ''
      }&redirect_uri=${config.redirectUri}&identity_provider=${config.identityProvider}&state=${encodeURIComponent(state)}${
        forceReauth ? `&prompt=${encodeURIComponent('login')}` : ''
      }`;
      console.log('Redirecting to auth URL:', authUrl);
      window.location.href = authUrl;
    },

    submitAuthCode: async (code: string, state?: string | null) => {
      const { config } = get();

      // Reject a callback we cannot verify. No `_authStore_DisableAutoLogin` flag is set: the
      // next load has no code, so auto-login re-runs with a fresh nonce and the flow self-heals.
      const rejectCallback = (reason: string) => {
        console.error(`Auth code rejected: ${reason}`);
        set({
          error: 'Login could not be verified. Please sign in again.',
          loginStatus: 'loggedOut',
          pendingAuthCode: null,
        });
        window.history.replaceState(null, '', window.location.pathname);
      };

      // `state` is mandatory here. This is only ever called with a `?code=` in the URL, and the
      // OTP flows never produce one - they hand the code straight to Amplify - so reaching this
      // point always means a federated callback, and requiring `state` cannot affect OTP tenants.
      //
      // Deploy-window cost: a user already mid-SSO-flow when this ships returns with a callback
      // that has no `state` and has to sign in once more. That is deliberate and far cheaper
      // than accepting unverifiable callbacks indefinitely - please do not re-open this for
      // backwards compatibility.
      const decodedState = decodeOAuthState(state);
      if (!decodedState) {
        rejectCallback('the callback carried no usable OAuth state');
        return;
      }

      // Fail closed: with no config there is no tenant to verify the callback against, so the
      // check cannot be performed and must not be skipped.
      if (!config) {
        rejectCallback('no tenant configuration is available to verify the callback against');
        return;
      }

      // Consume first, so a presented nonce is always burned even if the callback is rejected.
      const consumeResult = consumePendingSsoState(decodedState.nonce, decodedState.tenantId);

      if (decodedState.tenantId !== config.tenantId) {
        // The callback belongs to a different tenant than the one loaded - the original
        // HRI2-16956 symptom, which used to surface as a confusing "incorrect login method".
        rejectCallback(`the callback is for tenant ${decodedState.tenantId}, not ${config.tenantId}`);
        return;
      }

      if (consumeResult === 'mismatch' || consumeResult === 'absent') {
        // 'mismatch': this nonce is not one we minted, or is bound to another tenant.
        // 'absent': storage works and holds no pending attempt, so this browser never started
        // this sign-in. That is the login-CSRF case, and authorization-code single use does not
        // cover it - an attacker supplies a *fresh* code minted against their own IdP account.
        rejectCallback(`the login state could not be verified (${consumeResult})`);
        return;
      }

      if (consumeResult === 'unavailable') {
        // Deliberate trade-off (HRI2-16956): browser storage cannot be written at all here
        // (e.g. Safari private mode), so no pending record could ever have been kept and its
        // absence proves nothing. Rejecting would lock these users out - the exact failure class
        // this ticket fixes - so we accept the residual login-CSRF exposure for this narrow case.
        // PKCE is the proper closure for that residual gap and is tracked separately; it is not
        // something to solve by tightening this branch.
        console.warn('Login state could not be verified: browser storage is unavailable. Continuing.');
      }

      set({ pendingAuthCode: code, isLoading: true });

      try {
        console.log('Manually submitted auth code, exchanging code for tokens...');
        await get().exchangeCodeForToken(code);
        const storedAccessToken = sessionStorage.getItem(`${config?.tenantId}_authStore_AccessToken`);
        if (storedAccessToken) {
          allowAutoLogin(config?.tenantId);
          set({ loginStatus: 'loggedIn' });
        } else {
          set({ loginStatus: 'loggedOut' });
          disableAutoLogin(config?.tenantId);
        }
      } catch (_error) {
        set({ error: 'Error submitting auth code' });
        disableAutoLogin(config?.tenantId);
      } finally {
        set({ isLoading: false });
      }
    },

    exchangeCodeForToken: async (code: string) => {
      const { config } = get();
      if (!config) {
        console.error('No config provided for token exchange');
        return;
      }

      set({ isLoading: true });
      try {
        const response = await fetch(`${config.cognitoDomain}/oauth2/token`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
          body: new URLSearchParams({
            grant_type: 'authorization_code',
            client_id: config.userPoolClientId,
            code,
            redirect_uri: config.redirectUri,
          }),
        });
        const data = await response.json();

        if (data.access_token) {
          sessionStorage.setItem(`${config.tenantId}_authStore_AccessToken`, data.access_token);
          set({ accessToken: data.access_token });
        }
        if (data.refresh_token) {
          sessionStorage.setItem(`${config.tenantId}_authStore_RefreshToken`, data.refresh_token);
          set({ refreshToken: data.refresh_token });
        }
        set({ pendingAuthCode: null });
        window.history.replaceState(null, '', window.location.pathname);
        scheduleTokenRefresh();
      } catch (error) {
        console.error('Error exchanging auth code:', error);
        set({ error: 'Error exchanging auth code' });
        disableAutoLogin(config?.tenantId);
      } finally {
        set({ isLoading: false });
      }
    },

    refreshAccessToken: async () => {
      const { config, refreshToken } = get();
      if (!config || !refreshToken) {
        return;
      }
      try {
        const response = await fetch(`${config.cognitoDomain}/oauth2/token`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
          body: new URLSearchParams({
            grant_type: 'authStore_RefreshToken',
            client_id: config.userPoolClientId,
            refresh_token: refreshToken,
          }),
        });
        const data = await response.json();
        if (data.access_token) {
          sessionStorage.setItem(`${config.tenantId}_authStore_AccessToken`, data.access_token);
          set({ accessToken: data.access_token });
          scheduleTokenRefresh();
        }
      } catch (error) {
        console.error('Error refreshing access token:', error);
        set({ error: 'Error refreshing access token' });
      }
    },

    logout: () => {
      const { config } = get();
      sessionStorage.removeItem(`${config?.tenantId}_authStore_AccessToken`);
      sessionStorage.removeItem(`${config?.tenantId}_authStore_RefreshToken`);
      sessionStorage.removeItem(`${config?.tenantId}_authStore_LoginAttempted`);
      sessionStorage.removeItem(`${config?.tenantId}_authStore_Initialized`);
      disableAutoLogin(config?.tenantId);
      // An explicit sign-out is exactly when the user may want a different account, so make the
      // next sign-in re-authenticate at the IdP rather than silently reusing the live session.
      requireForceReauth(config?.tenantId);

      set({
        accessToken: null,
        refreshToken: null,
        loginStatus: 'loggedOut',
        otpRequestInfo: null,
        hasInitialized: false,
        pendingAuthCode: null,
      });

      signOut();

      if (config) {
        window.location.href = `${config.cognitoDomain}/logout?client_id=${config.userPoolClientId}&logout_uri=${config.redirectUri}`;
      }
    },

    /**
     * Tears down a session that authenticated successfully but is not usable (for example a user
     * that still exists in Cognito after being removed from the backend). Unlike logout() this does
     * not redirect anywhere and deliberately leaves `error` in place, so the message stays on
     * screen while the invalid session data is discarded behind it.
     */
    clearInvalidSession: async () => {
      const { accessToken, config, loginStatus } = get();
      if (loginStatus === 'loggedOut' && !accessToken) {
        return;
      }

      clearTenantAuthSession(config?.tenantId);
      // Without this the next render would auto-redirect to a still-valid IdP session and
      // re-authenticate the same unusable user, looping between the app and the IdP.
      disableAutoLogin(config?.tenantId);
      // And when the user does sign in again, make it re-authenticate at the identity provider.
      // Cognito's /logout ends only its own hosted-UI session, so without this the IdP re-asserts
      // the same unusable identity and "sign in with a different account" cannot reach another one.
      requireForceReauth(config?.tenantId);

      // Amplify keeps the Cognito tokens in localStorage, so this is what stops the invalid session
      // from being restored on the next visit. It does not redirect, as no hosted UI is configured,
      // but it does revoke the refresh token server-side when the access token carries an
      // origin_jti, which is the case for the hosted-UI session this path deals with.
      try {
        await signOut();
      } catch (error) {
        // Most commonly a missing token provider config, in which case nothing was cleared. Leave
        // the store as it is rather than reporting a logged-out session that Amplify still holds
        // tokens for; the next teardown attempt then retries instead of short-circuiting.
        console.error('Error clearing invalid session:', error);
        return;
      }

      set({
        accessToken: null,
        refreshToken: null,
        loginStatus: 'loggedOut',
        otpRequestInfo: null,
        hasInitialized: false,
        pendingAuthCode: null,
      });
    },

    requestOTP: async (username: string) => {
      // As in login(): a stale error must not outlive the attempt that produced it.
      set({ isLoading: true, error: null });

      try {
        const currentUser = await getCurrentUser().catch(() => null);
        if (currentUser) {
          await signOut();
        }

        const result = await signIn({
          username,
          options: { authFlowType: 'CUSTOM_WITHOUT_SRP' },
        });

        const receivedAt = new Date();
        const expiry = new Date(receivedAt.getTime() + 5 * 60 * 1000);

        set({
          loginStatus: 'otpRequested',
          otpRequestInfo: {
            username,
            otpResponse: result,
            expiry,
            receivedAt,
          },
        });
        return result;
      } catch (error) {
        console.error('Error requesting OTP:', error);
        throw error;
      } finally {
        set({ isLoading: false });
      }
    },

    signInWithOTP: async (code: string) => {
      set({ isLoading: true });
      const { config } = get();

      try {
        const result = await confirmSignIn({ challengeResponse: code });
        await get().refreshAccessToken();

        const session = await fetchAuthSession({ forceRefresh: true });
        const sessionTenantCode = session.tokens?.accessToken?.payload?.['custom:tenantCode'];

        /*
         * The sign-in succeeded, but against a different tenant than the portal URL this learner is
         * on - the tenant is the first path segment, so following a link meant for another
         * organization lands them here with a perfectly valid account.
         *
         * Nothing above this point may mark them signed in. It used to: loginStatus became
         * 'loggedIn' and auto-login was enabled for this tenant before the check ran, so throwing
         * left the store claiming a signed-in session with no access token and the app waited for
         * one that was never coming. Auto-login being left on for the wrong tenant then dropped
         * them back into the same state on their next visit.
         */
        if (sessionTenantCode !== config?.tenantId) {
          disableAutoLogin(config?.tenantId);
          await signOut().catch(() => undefined);

          set({
            loginStatus: 'loggedOut',
            otpRequestInfo: null,
            accessToken: null,
            refreshToken: null,
          });

          throw new WrongTenantError(typeof sessionTenantCode === 'string' ? sessionTenantCode : undefined);
        }

        set({ loginStatus: 'loggedIn', otpRequestInfo: null });
        allowAutoLogin(config?.tenantId);

        set({
          accessToken: session.tokens?.accessToken.toString(),
          refreshToken: null,
        });
        return result;
      } catch (error) {
        console.error('Error signing in with OTP:', error);
        throw error;
      } finally {
        set({ isLoading: false });
      }
    },

    /**
     * Leaves the OTP step (Back, or the code expiring) for the email step. The router shows the OTP
     * screen for as long as loginStatus is 'otpRequested', so clearing only otpRequestInfo left that
     * screen mounted with nothing to render but its fallback spinner (HRI2-18977).
     *
     * Guarded so a late expiry timer cannot sign out a learner who has already logged in.
     */
    resetOtpRequestInfo: () => {
      set((state) =>
        state.loginStatus === 'otpRequested'
          ? { otpRequestInfo: null, loginStatus: 'loggedOut' }
          : { otpRequestInfo: null },
      );
    },

    setError: (error: string | null) => {
      set({ error });
    },
  };
});
